import React from "react"
import { navigate } from "gatsby"
import { Grid, Box, Button } from "@material-ui/core"
import { Alert } from "@material-ui/lab"
import { useForm } from "react-hook-form"
import firebase from "../Firebase"
import Wraptypo from "../components/WrapTypo"
import { getThemeProps } from "@material-ui/styles"

import { useTheme } from "@material-ui/core/styles"
import useMediaQuery from "@material-ui/core/useMediaQuery"

const isEmpty = obj => !Object.keys(obj).length

const emailRule = {
  pattern: {
    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
    message: "「メールアドレス」の入力形式が正しくありません。",
  },
}
const phoneNumberRule = {
  minLength: {
    value: 6,
    message: "「電話番号」の入力文字数が少なすぎます",
  },
  maxLength: {
    value: 12,
    message: "「電話番号」の入力文字数が多すぎます",
  },
  pattern: {
    value: /^[0-9]+$/,
    message: "「電話番号」の入力形式が正しくありません",
  },
}

const textRule = name => ({
  maxLength: {
    value: 50,
    message: `「${name}」の入力文字数が多すぎます`,
  },
})

const contextRule = {
  maxLength: {
    value: 1000,
    message: "「内容」入力文字数が多すぎます",
  },
  pattern: {
    value: /[぀-ゟ゠-ヿ一-鿿]/,
    message: "「内容」に日本語(ひらがな・カタカナ・漢字)を含めてください",
  },
}

const positionRule = {
  maxLength: {
    value: 100,
    message: "「Position/役職」の入力文字数が多すぎます",
  },
}

const MIN_SUBMIT_SECONDS = 3
const RATE_LIMIT_MAX = 3
const RATE_LIMIT_WINDOW_MS = 60 * 60 * 1000
const RATE_LIMIT_KEY = "contact_submit_history"

const urlPatternForShortFields = /(https?:\/\/|www\.|\.(com|net|org|ru|cn|xyz|top|info|biz|club|online|site)\b)/i
const cyrillicPattern = /[Ѐ-ӿ]/

const detectSpam = values => {
  if (urlPatternForShortFields.test(values.company || "")) return true
  if (urlPatternForShortFields.test(values.name || "")) return true
  if (urlPatternForShortFields.test(values.phoneNumber || "")) return true

  const urlMatches = (values.context || "").match(/https?:\/\/[^\s]+/gi) || []
  if (urlMatches.length >= 3) return true

  const allValues = Object.values(values).join(" ")
  if (cyrillicPattern.test(allValues)) return true

  return false
}

const checkRateLimit = () => {
  if (typeof window === "undefined") return true
  try {
    const now = Date.now()
    const raw = window.localStorage.getItem(RATE_LIMIT_KEY)
    const history = raw ? JSON.parse(raw) : []
    const recent = history.filter(t => now - t < RATE_LIMIT_WINDOW_MS)
    if (recent.length >= RATE_LIMIT_MAX) return false
    recent.push(now)
    window.localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recent))
    return true
  } catch (e) {
    return true
  }
}

export const ContactForm = () => {
  const { handleSubmit, register, errors } = useForm()
  const [loading, setLoading] = React.useState(false)
  const formLoadedAt = React.useRef(Date.now())

  const onSubmit = values => {
    setLoading(true)

    const elapsedSec = (Date.now() - formLoadedAt.current) / 1000
    const isBot =
      values.website ||
      elapsedSec < MIN_SUBMIT_SECONDS ||
      detectSpam(values) ||
      !checkRateLimit()

    if (isBot) {
      setTimeout(() => {
        navigate("/contacts-result/", { state: { result: "success" } })
        setLoading(false)
      }, 500)
      return
    }

    const { website, ...cleanValues } = values
    const docId = firebase.firestore().collection("contacts").doc().id
    firebase
      .firestore()
      .collection("contacts")
      .doc(docId)
      .set({
        ...cleanValues,
        created_at: firebase.firestore.FieldValue.serverTimestamp(),
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      })
      .then(() => {
        navigate("/contacts-result/", { state: { result: "success" } })
        setLoading(false)
      })
      .catch(e => {
        navigate("/contacts-result/", { state: { result: "faild" } })
        setLoading(false)
      })
  }

  const errKeys = Object.keys(errors)
  return (
    <>
      <Box width="100%" maxWidth="1080px" margin="auto">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
          >
            <label>
              Website (この項目は空欄のままにしてください)
              <input
                type="text"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                ref={register}
              />
            </label>
          </div>
          <>
            {!isEmpty(errors) && errKeys.length > 0 && (
              <Box mb={2}>
                <Alert severity="error">
                  {errKeys.map(k => (
                    <React.Fragment key={k}>
                      ・{errors[k].message}
                      <br />
                    </React.Fragment>
                  ))}
                </Alert>
              </Box>
            )}
            <Box width="100%" padding="0 30px">
              <GridResponsiveSpace>
                <Grid item xs={12} sm={6}>
                  <Box style={{ textAlign: "left" }}>
                    <InputFormItem
                      title="Name/名前"
                      placeholder="お名前"
                      name="company"
                      register={register}
                      rule={textRule("会社名")}
                      requiredItemFlag={true}
                      subTitle="入力必須項目です。"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box style={{ textAlign: "left" }}>
                    <InputFormItem
                      title="Company/会社名・所属組織名"
                      placeholder="一般社団法人Public Meets Innovation"
                      name="name"
                      register={register}
                      rule={textRule("お名前")}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box style={{ textAlign: "left" }}>
                    <InputFormItem
                      title="Position/役職"
                      placeholder="役職"
                      name="phoneNumber"
                      register={register}
                      rule={positionRule}
                      optional={true}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box style={{ textAlign: "left" }}>
                    <InputFormItem
                      name="mail"
                      title="Mail/メールアドレス"
                      placeholder="email@example"
                      register={register}
                      rule={emailRule}
                      requiredItemFlag={true}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box style={{ textAlign: "left" }}>
                    <InputFormItem
                      name="tel"
                      title="Tel/電話番号"
                      placeholder="090xxxxxxxx"
                      register={register}
                      rule={phoneNumberRule}
                      requiredItemFlag={true}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box style={{ textAlign: "left" }}>
                    <SelectFormItem
                      name="employeeRange"
                      title="依頼内容"
                      optionsText={["-", "取材", "講演", "お仕事", "その他"]}
                      register={register}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box style={{ textAlign: "left" }}>
                    <TextAreaFormItem
                      name="context"
                      title="Inquiry/お問い合わせ内容"
                      placeholder="内容を記入してください"
                      register={register}
                      rule={contextRule}
                      requiredItemFlag={true}
                    />
                  </Box>
                </Grid>
              </GridResponsiveSpace>
            </Box>
            <Box textAlign="center">
              {!loading ? (
                <Button
                  type="submit"
                  style={{ backgroundColor: "#80B7F5", borderRadius: "0" }}
                >
                  <Wraptypo mt={"8px"} mb={"8px"} color="#ffffff">
                    入力内容を送信
                  </Wraptypo>
                </Button>
              ) : (
                <Button
                  disabled={true}
                  type="submit"
                  style={{ backgroundColor: "#80B7F5", borderRadius: "0" }}
                >
                  <Wraptypo mt={"8px"} mb={"8px"} color="#ffffff">
                    送信中
                  </Wraptypo>
                </Button>
              )}
            </Box>
          </>
        </form>
      </Box>
      <Box mb={4} />
    </>
  )
}

const InputFormItem = ({
  title,
  placeholder,
  name,
  register,
  rule,
  requiredItemFlag,
  subTitle,
  optional,
}) => {
  return (
    <>
      <Grid container>
        <Box width="100%">
          <Grid item>
            <Wraptypo fs="14px">
              {title}
              <span
                style={
                  requiredItemFlag ? { color: "red" } : { display: "none" }
                }
              >
                *
              </span>{" "}
              {subTitle}
            </Wraptypo>
          </Grid>
          <Grid item>
            <input
              type="text"
              name={name}
              ref={register({
                ...(optional
                  ? {}
                  : { required: `「${title}」の項目が未入力です` }),
                ...rule,
              })}
              placeholder={placeholder}
              style={{
                height: "30px",
                fontSize: "16px",
                width: "100%",
                border: "0",
                fontSize: "14px",
                padding: "0 10px",
              }}
            />
          </Grid>
        </Box>
      </Grid>
      <Box mb={4} />
    </>
  )
}

const TextAreaFormItem = ({
  title,
  placeholder,
  name,
  register,
  rule,
  requiredItemFlag,
  subTitle,
}) => {
  return (
    <>
      <Grid container>
        <Box width="100%">
          <Wraptypo fs="14px">
            {title}
            <span
              style={requiredItemFlag ? { color: "red" } : { display: "none" }}
            >
              *
            </span>{" "}
            {subTitle}
          </Wraptypo>
          <Grid item>
            <textarea
              name={name}
              ref={register({
                required: `「${title}」の項目が未入力です`,
                ...rule,
              })}
              placeholder={placeholder}
              style={{
                height: "100px",
                fontSize: "16px",
                width: "100%",
                border: "0",
                fontSize: "14px",
                padding: "5px 10px",
              }}
            />
          </Grid>
        </Box>
      </Grid>
      <Box mb={4} />
    </>
  )
}

const SelectFormItem = ({
  title,
  optionsText,
  register,
  name,
  requiredItemFlag,
  subTitle,
}) => {
  return (
    <>
      <Grid container>
        <Box width="100%">
          <Grid item>
            <Wraptypo color="#999" fs="14px">
              {title}
              <span
                style={
                  requiredItemFlag ? { color: "red" } : { display: "none" }
                }
              >
                *
              </span>{" "}
              {subTitle}
            </Wraptypo>
          </Grid>
          <Grid item>
            <select
              name={name}
              ref={register({ required: `「${title}」の項目が未選択です` })}
              style={{
                width: "100%",
                height: "30px",
                fontSize: "16px",
                border: "0",
                fontSize: "14px",
              }}
            >
              {optionsText.map(x => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </Grid>
        </Box>
      </Grid>
      <Box mb={4} />
    </>
  )
}

const GridResponsiveSpace = ({ children }) => {
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down("xs"))

  return (
    <Grid container spacing={isSmall ? 0 : 3}>
      {children}
    </Grid>
  )
}
